<script>
  import { getContext, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Link, link, useNavigate } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row, Tooltip } from '@sveltestrap/sveltestrap';

  import { BulkActionModal } from '@client/components/bulk-action-modal';
  import { EmptyState } from '@client/components/empty-state';
  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';
  import { RouteSelectionControls } from '@client/components/route-selection-controls';
  import { Switch } from '@client/components/switch';

  import { TargetingService } from '@client/services/targeting';

  import { createSelection, selectedItems } from '@client/utils/selection';

  import { deepCopyArray } from '@common/utils/arrays';

  import { EditModal } from './modals';
  import { Route } from './route';
  import { buildDepartmentsPayload, getTargetKey, removeTargets, setTargetStatus } from './shared';

  const { basepath } = getContext('config');
  const { addNotification } = getNotificationsContext();

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  export let campaigns = [];
  export let groups = [];
  export let tags = [];
  export let loading = false;

  let sortBy = 'status';
  let sortDirection = -1;

  let selectedGroup = {};
  let showEdit = false;

  let initialized = false;

  let routes = [];
  let priority = [];
  let reordering = false;
  let dropIndex = null;
  let startIndex = null;

  let startPosition = null;
  let targetPosition = null;

  let targeting = {};
  let selectedRoute = {};
  let matchAll = false;

  const selection = createSelection(getTargetKey);

  const BULK_NOTIFY = {
    enable: 'label.BULK_ACTION.NOTIFY_ENABLE_TARGETS',
    disable: 'label.BULK_ACTION.NOTIFY_DISABLE_TARGETS',
    delete: 'label.BULK_ACTION.NOTIFY_DELETE_TARGETS'
  };

  let selecting = false;
  let showBulkAction = false;
  let bulkAction = '';
  let bulkItems = [];

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const transformGroupRoutes = () => {
    return targeting.departments.map((rule, index) => {
      rule.groups = rule.sendergroups.map(({ groupname }) => ({ label: groupname, value: groupname }));
      rule.campaigns = rule.ads.map(({ text }) => ({ label: text, value: text }));
      rule.tags = rule.tags.map(({ text }) => ({ label: text, value: text }));
      rule.priority = index + 1;
      rule.target = rule.targettype;
      rule.status = !!rule.status;
      rule._id = rule._id;

      return rule;
    });
  };

  const updatePriorityPositions = (list, start, target) => {
    if (start < target) {
      list.splice(target + 1, 0, list[start]);
      list.splice(start, 1);
    } else {
      if (target === -1) {
        list.splice(start - 1, 1);
      } else {
        list.splice(target, 0, list[start]);
        list.splice(start + 1, 1);
      }
    }

    list.forEach((item, index) => {
      if (item.priority !== null) {
        item.priority = index + 1;
      }
    });

    return list;
  };

  const updatePriority = (which) => {
    priority = deepCopyArray(which === 'routes' ? routes : priority);

    if (which !== 'routes') {
      routes = deepCopyArray(priority);
    }
  };

  const updatePositions = () => {
    priority = updatePriorityPositions(priority, Number(startPosition) - 1, Number(targetPosition) - 1);
    startPosition = targetPosition = null;
  };

  const onCancelReorder = () => {
    reordering = false;
    updatePriority('routes');
  };

  const onAdd = () => {
    selectedRoute = {
      target: 'sender',
      campaigns: [],
      groups: [],
      tags: [],
      status: false
    };

    showEdit = true;
  };

  const onEdit = (route) => {
    selectedRoute = { ...route };
    showEdit = true;
  };

  const onUpdate = (route) => {
    const index = priority.findIndex((item) => item._id === route._id);

    if (index > -1) {
      priority[index] = route;
    } else {
      priority.push(route);
      priority = updatePriorityPositions(priority, priority.length - 1, 0);
    }

    onSave();

    showEdit = false;
  };

  const onToggleMatchMode = () => {
    targeting.matchmode = matchAll ? 'all' : 'waterfall';
    onSave();
  };

  // Targeting is one document, so every save sends the full list of targets.
  const persist = async (departments) => {
    const response = await TargetingService.update({
      accounts: targeting.accounts,
      matchmode: targeting.matchmode,
      departments: buildDepartmentsPayload(departments)
    });

    return !(response?.status >= 400);
  };

  const refresh = async () => {
    targeting = await TargetingService.get();
    matchAll = targeting.matchmode === 'all';
    routes = transformGroupRoutes();
    priority = deepCopyArray(routes);
    reordering = false;
  };

  const onSave = async () => {
    showEdit = false;

    loading = true;

    const saved = await persist(priority);

    if (saved) {
      notify($format('label.SUCCESS.TARGETING_UPDATED'));
    } else {
      notify($format('label.FAILURE.TARGETING_UPDATE'), 'error');
    }

    await refresh();

    loading = false;
  };

  // Enable and Disable act only on the targets that need changing, so a mixed
  // selection can offer both.
  const onBulkAction = (action) => {
    bulkItems =
      action === 'delete'
        ? selectedTargets
        : selectedTargets.filter(({ status }) => (action === 'enable' ? !status : status));
    bulkAction = action;
    showBulkAction = true;
  };

  const runBulkAction = (items) => {
    const keys = new Set(items.map(getTargetKey));
    const next =
      bulkAction === 'delete'
        ? removeTargets(priority, keys)
        : setTargetStatus(priority, keys, bulkAction === 'enable');

    return persist(next);
  };

  const onBulkActionFinish = async (failed) => {
    const succeeded = bulkItems.length - failed.length;

    if (succeeded) {
      notify($format(BULK_NOTIFY[bulkAction], { values: { count: succeeded } }));
    }

    // Leave only the failures selected so they can be retried directly.
    if (failed.length) {
      selection.selectAll(failed);
    } else {
      selecting = false;
      selection.clear();
    }

    loading = true;
    await refresh();
    loading = false;
  };

  const onDelete = async (route) => {
    const confirmed = confirm($format('label.DELETE_ROUTE_CONFIRMATION'));

    if (confirmed) {
      priority = updatePriorityPositions(priority, route.priority, -1);
      onSave();
    }
  };

  const onReset = () => {
    reordering = false;
    showEdit = false;
    updatePriority('routes');
  };

  onMount(async () => {
    loading = true;

    targeting = await TargetingService.get();

    matchAll = targeting.matchmode === 'all';

    routes = transformGroupRoutes();

    priority = deepCopyArray(routes);

    initialized = true;

    loading = false;
  });

  $: hasTargeting = initialized && priority.length > 0;
  $: selectedTargets = selectedItems(priority, $selection, getTargetKey);
  // Keeps the count honest when a refresh drops selected targets.
  $: selecting && selection.prune(priority);
  $: bulkConfig =
    {
      enable: {
        title: $format('label.BULK_ACTION.CONFIRM_ENABLE_TARGETS', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_ENABLE_TARGETS'),
        confirmLabel: $format('label.ENABLE'),
        confirmColor: 'primary'
      },
      disable: {
        title: $format('label.BULK_ACTION.CONFIRM_DISABLE_TARGETS', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_DISABLE_TARGETS'),
        confirmLabel: $format('label.DISABLE'),
        confirmColor: 'primary'
      },
      delete: {
        title: $format('label.BULK_ACTION.CONFIRM_DELETE_TARGETS', { values: { count: bulkItems.length } }),
        warning: $format('label.BULK_ACTION.WARNING_DELETE_TARGETS'),
        confirmLabel: $format('label.DELETE'),
        confirmColor: 'danger'
      }
    }[bulkAction] || {};
  $: disableManualMove =
    !startPosition ||
    !targetPosition ||
    Number(startPosition) === Number(targetPosition) ||
    Number(targetPosition) > priority.length ||
    Number(startPosition) < 1 ||
    Number(targetPosition) < 1;
</script>

<div id="targeting-groups" class="page">
  <Loader backdrop show={loading} />
  <div class="page-header no-bottom-border">
    <Container fluid>
      <Row>
        <Col>
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/campaigns" use:link>{$format('label.MARKETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/marketing/targeting" use:link>{$format('label.TARGETING')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.GROUPS')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row style="max-width: {hasTargeting ? '1268px' : '100%'};">
        <Col xs="6" sm="6" md="6">
          <h1 class="page-title">{$format('label.GROUPS')}</h1>
        </Col>
        <Col class="text-right">
          {#if initialized}
            <Button color="secondary" on:click={onAdd}>
              {$format('label.ADD_GROUP_TARGET')}
            </Button>
          {/if}
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="tabs-container">
      <div class="tabs">
        <div class="tab-list">
          <Link class="tab active" to="/marketing/targeting/groups">{$format('label.GROUPS')}</Link>
          <Link class="tab" to="/marketing/targeting/domains">{$format('label.DOMAINS')}</Link>
          <Link class="tab" to="/marketing/targeting/recipients">{$format('label.RECIPIENTS')}</Link>
          <Link class="tab" to="/marketing/targeting/diagnostics">{$format('label.PREVIEW')}</Link>
        </div>
      </div>
      <div class="filters"></div>
    </div>
    <div class="routes-layout">
      {#if hasTargeting}
        <div class="match-mode">
          <h2 class="match-mode-title">
            {$format('label.MATCH_MODE')}
            <Icon name="help-circle" id="tooltip-match-mode" />
            <Tooltip target="tooltip-match-mode" placement="top">
              {$format('label.TOOLTIP.MATCH_MODE')}
            </Tooltip>
          </h2>
          <label class="match-mode-switch">
            <Switch bind:checked={matchAll} on:change={onToggleMatchMode} />
            <span>{$format('label.ALL_MATCHES')}</span>
          </label>
          <p class="match-mode-state" data-testid="match-mode-state">
            {$format(matchAll ? 'label.MATCH_MODE_ALL_DESCRIPTION' : 'label.MATCH_MODE_WATERFALL_DESCRIPTION')}
          </p>
        </div>

        <div class="routes-controls sticky-header">
          <div class="actions">
            {#if !reordering}
              <RouteSelectionControls
                bind:selecting
                {selection}
                rows={priority}
                selected={selectedTargets}
                id="group-targets"
                onAction={onBulkAction}
                onSelectAll={() => selection.selectAll(priority)}
              />
            {/if}

            {#if !reordering && !selecting}
              <Button color="secondary" on:click={() => (reordering = true)}>
                {@html $format('label.CHANGE_PRIORITY')}
              </Button>
            {:else if reordering}
              <Button color="secondary" on:click={onCancelReorder}>
                {$format('label.CANCEL')}
              </Button>
              <Button color="primary" on:click={onSave}>{$format('label.SAVE')}</Button>
            {/if}
          </div>
        </div>
        <div class="priority">
          <div class="priority-list">
            {#if reordering}
              <div class="manual-move">
                <span>{$format('label.MOVE')}</span>
                <input type="number" bind:value={startPosition} min="1" max={priority.length} placeholder="1" />
                <span>{$format('label.MOVE_TO')}</span>
                <span class="hide--mobile">{$format('label.POSITION').toLowerCase()}</span>
                <input type="number" bind:value={targetPosition} min="1" max={priority.length} placeholder="2" />
                <Button size="sm" color="secondary" on:click={updatePositions} disabled={disableManualMove}>
                  {$format('label.UPDATE')}
                </Button>
              </div>
            {/if}
            {#each priority as route, index}
              <Route
                bind:reordering
                bind:priority
                bind:dropIndex
                bind:startIndex
                routeIndex={index}
                onUpdate={onSave}
                {onEdit}
                {onDelete}
                {route}
                {updatePriorityPositions}
                {selecting}
                selected={$selection.has(getTargetKey(route))}
                onSelect={selection.toggle}
              />
            {/each}
          </div>
        </div>
      {:else if initialized}
        <EmptyState
          icon="users"
          heading={$format('label.EMPTY_STATE.TARGETING_GROUPS_HEADING')}
          description={$format('label.EMPTY_STATE.TARGETING_GROUPS_DESCRIPTION')}
          action={$format('label.EMPTY_STATE.TARGETING_GROUPS_ACTION')}
          on:click={onAdd}
        />
      {/if}
    </div>
  </div>
</div>

{#if showEdit}
  <EditModal bind:show={showEdit} bind:route={selectedRoute} bind:campaigns bind:tags bind:groups {onUpdate} />
{/if}

{#if showBulkAction}
  <BulkActionModal
    bind:show={showBulkAction}
    items={bulkItems}
    title={bulkConfig.title}
    warning={bulkConfig.warning}
    confirmLabel={bulkConfig.confirmLabel}
    confirmColor={bulkConfig.confirmColor}
    progressLabel={$format('label.BULK_ACTION.PROGRESS_TARGETING')}
    leaveMessage={$format('label.BULK_ACTION.CONFIRM_LEAVE_TARGETING')}
    getName={({ priority: position }) => `${$format('label.GROUP_TARGET')} ${position}`}
    onRun={runBulkAction}
    onFinish={onBulkActionFinish}
    batch
  />
{/if}

<style lang="scss" src="./groups.scss"></style>
